import React from 'react';

/**
 * Modern, high-performance UnifiedLoader component for Pfadfinder Dashboard & Website.
 * Clean, minimalist aesthetic with silky-smooth SVG stroke animation and backdrop blur.
 * 
 * @param {string} variant - 'fullscreen', 'overlay', 'inline', 'button'
 * @param {string} text - Optional loading text
 * @param {string} color - Optional color override
 * @param {number} size - Optional size multiplier
 * @param {boolean} fullscreen - Legacy support for boolean prop
 */
const UnifiedLoader = ({ 
    variant = 'inline', 
    text = 'Wird geladen...', 
    color, 
    size = 1,
    fullscreen = false
}) => {
    const actualVariant = fullscreen ? 'fullscreen' : variant;

    // Small inline spinner for buttons
    if (actualVariant === 'button') {
        return (
            <svg 
                className="animate-spin" 
                style={{ width: `${18 * size}px`, height: `${18 * size}px` }} 
                viewBox="0 0 24 24" 
                fill="none"
            >
                <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke={color || "currentColor"} 
                    strokeWidth="3" 
                />
                <path 
                    className="opacity-90" 
                    fill={color || "currentColor"} 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" 
                />
            </svg>
        );
    }

    const containerClass = actualVariant === 'fullscreen' 
        ? 'unified-loader-fullscreen' 
        : actualVariant === 'overlay' 
            ? 'unified-loader-overlay' 
            : 'unified-loader-container';

    const spinnerSize = actualVariant === 'fullscreen' ? Math.round(52 * size) : Math.round(40 * size);

    return (
        <div className={containerClass}>
            <div className="unified-loader-spinner-wrapper" style={{ width: spinnerSize, height: spinnerSize }}>
                <svg 
                    className="unified-loader-svg" 
                    viewBox="0 0 50 50"
                    style={{ width: spinnerSize, height: spinnerSize }}
                >
                    <defs>
                        <linearGradient id="unified-loader-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor={color || "rgb(var(--color-primary))"} />
                            <stop offset="100%" stopColor={color || "rgb(var(--color-tertiary, var(--color-primary)))"} stopOpacity="0.85" />
                        </linearGradient>
                    </defs>
                    {/* Background track */}
                    <circle 
                        className="unified-loader-track" 
                        cx="25" 
                        cy="25" 
                        r="20" 
                        fill="none" 
                        strokeWidth="3.5" 
                    />
                    {/* Active spinning arc with rounded stroke caps */}
                    <circle 
                        className="unified-loader-arc" 
                        cx="25" 
                        cy="25" 
                        r="20" 
                        fill="none" 
                        stroke="url(#unified-loader-gradient)" 
                        strokeWidth="3.5" 
                        strokeLinecap="round" 
                    />
                </svg>
                {/* Subtle centered pulse dot */}
                <div className="unified-loader-center-dot" style={{ backgroundColor: color || undefined }} />
            </div>

            {text && (
                <div className="unified-loader-text-wrapper">
                    <span className="unified-loader-text-clean">{text}</span>
                </div>
            )}
        </div>
    );
};

export default UnifiedLoader;
